import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
} from '@angular/core';
import { Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { Datastore, DatastoreDocument } from '@freelancer/datastore';
import type {
  UserTaxInfo,
  UserTaxInfoCollection,
  UsersSelf,
  UsersSelfCollection,
  UserSkill,
  UserSkillsCollection,
  VerificationProcessTypesCollection,
  UserDocumentSubmissionsCollection,
} from '@freelancer/datastore/collections';
import { VERIFICATION_PROCESS_TYPES } from '@freelancer/datastore/collections';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { ProgressColor, ProgressSize } from '@freelancer/ui/progress-bar';
import { isDefined } from '@freelancer/utils';
import { TaxTypeApi } from 'api-typings/common/common';
import { UserDocumentSubmissionStatusApi } from 'api-typings/users/users';
import { EquipmentGroupName } from 'app/jobs/job-post/freightlancer/form/freightlancer-form.model';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-freightlancer-complete-your-profile-widget',
  template: `
    @if (showWidget$ | flAsync) {
      <fl-card class="mb-small">
        <div class="HeaderContent mb-small">
          <p
            class="text-body-16 font-medium"
            i18n="Complete your profile widget title"
          >
            Set up your account
          </p>
          <p>{{ (progressPercentage$ | flAsync) ?? 0 }}%</p>
        </div>
        <fl-progress-bar
          class="mb-small"
          label="Complete your profile"
          i18n-label="Complete your profile's progress bar label"
          [color]="ProgressColor.DEFAULT"
          [size]="ProgressSize.LARGE"
          [progressPercentage]="(progressPercentage$ | flAsync) ?? 0"
        >
        </fl-progress-bar>
        <p
          class="mb-small"
          i18n="Complete your profile widget description"
        >
          Having a complete, verified account will increase your chances of
          getting jobs.
        </p>
        <fl-button
          i18n="Complete account button"
          flTrackingLabel="CompleteYourProfileButton"
          flTrackingSection="CompleteYourProfileButton"
          [color]="ButtonColor.PRIMARY"
          [display]="'block'"
          [size]="ButtonSize.LARGE"
          (click)="openCompleteYourProfilePage()"
        >
          Complete account
        </fl-button>
      </fl-card>
      @if (onDashboard) {
        <fl-hr class="mb-small" />
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./complete-your-profile-widget.component.scss'],
})
export class CompleteYourProfileWidgetComponent implements OnInit {
  private auth = inject(Auth);
  private datastore = inject(Datastore);
  private router = inject(Router);

  @Input() onDashboard = false;

  readonly PROFILE_COMPLETION_STEPS = 6;

  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  ProgressColor = ProgressColor;
  ProgressSize = ProgressSize;

  usersSelfDocument: DatastoreDocument<UsersSelfCollection>;
  userSkillsDocument: DatastoreDocument<UserSkillsCollection>;
  progressPercentage$: Observable<number>;
  showWidget$: Observable<boolean>;

  ngOnInit(): void {
    this.usersSelfDocument = this.datastore.document<UsersSelfCollection>(
      'usersSelf',
      this.auth.getUserId(),
    );

    this.userSkillsDocument = this.datastore.document<UserSkillsCollection>(
      'userSkills',
      this.auth.getUserId(),
    );

    const verificationProcess$ = this.datastore
      .collection<VerificationProcessTypesCollection>(
        'verificationProcessTypes',
        query =>
          query.where(
            'url',
            '==',
            VERIFICATION_PROCESS_TYPES.LOADSHIFT_CARRIER_VERIFICATION,
          ),
      )
      .valueChanges()
      .pipe(
        map(processes => processes[0]),
        filter(isDefined),
      );

    const userDocumentSubmissionStatus$ = this.datastore
      .collection<UserDocumentSubmissionsCollection>(
        'userDocumentSubmissions',
        query =>
          query.search(
            verificationProcess$.pipe(
              map(process => ({ verificationProcessTypeIds: process.id })),
            ),
          ),
      )
      .valueChanges()
      .pipe(map(submissions => submissions[0]?.status));

    const userTaxInfoAbn$ = this.datastore
      .collection<UserTaxInfoCollection>('userTaxInfo', query =>
        query.where('type', '==', TaxTypeApi.ABN),
      )
      .valueChanges();

    this.progressPercentage$ = combineLatest([
      this.usersSelfDocument.valueChanges(),
      this.userSkillsDocument.valueChanges(),
      userTaxInfoAbn$,
      userDocumentSubmissionStatus$,
    ]).pipe(
      map(([usersSelf, userSkills, abnTaxInfos, docStatus]) =>
        this.computeProfileProgressPercentage(
          usersSelf,
          userSkills,
          abnTaxInfos,
          docStatus,
        ),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.showWidget$ = this.progressPercentage$.pipe(
      map(progress => progress < 100),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  openCompleteYourProfilePage(): void {
    this.router.navigate(['/complete-your-profile'], {
      queryParams: {
        next: encodeURIComponent(this.router.url),
      },
      replaceUrl: true,
    });
  }

  private computeProfileProgressPercentage(
    usersSelf: UsersSelf,
    userSkills: UserSkill,
    abnTaxInfos: readonly UserTaxInfo[],
    docStatus: UserDocumentSubmissionStatusApi | undefined,
  ): number {
    let completed = 0;

    if (usersSelf.status.emailVerified) {
      completed += 1;
    }

    const hasAbn = abnTaxInfos.some(tax => {
      const normalized = tax.number?.replace(/\s/g, '') ?? '';
      return normalized.length !== 0;
    });
    if (hasAbn) {
      completed += 1;
    }

    if (usersSelf.status.identityVerified) {
      completed += 1;
    } else if (
      docStatus === UserDocumentSubmissionStatusApi.ACTIVE ||
      docStatus === UserDocumentSubmissionStatusApi.IN_REVIEW
    ) {
      completed += 1;
    }

    if ((userSkills.skills ?? []).length > 0) {
      completed += 1;
    }

    const vehicleTypeGroup = usersSelf.equipmentGroups?.find(
      group => group.name === EquipmentGroupName.VEHICLE_TYPE,
    );
    if ((vehicleTypeGroup?.items ?? []).length > 0) {
      completed += 1;
    }

    if ((usersSelf.operatingAreas ?? []).length > 0) {
      completed += 1;
    }

    return Math.min(
      100,
      Math.max(
        0,
        Math.round((completed / this.PROFILE_COMPLETION_STEPS) * 100),
      ),
    );
  }
}
